import { createContext, FunctionComponent, PropsWithChildren, useEffect } from 'react';
import { useContext, useState, useMemo } from 'react';
import { MediaGalleryLightBox } from '../../components/MediaGalleryLightBox/MediaGalleryLightBox';
import { useFeatureRollout } from '@rea-argonaut/experiment';
import { MediaGalleryModal } from '../../components/MediaGalleryModal/MediaGalleryModal';

const mediaGalleryHash = '#mediaGallery';

export interface IMediaGalleryModal {
  openAction: 'MediaShowcaseBrick:Click' | 'TimelineMedia:Click' | 'DeepLink';
  initialImageIndex?: number;
  galleryItemKey?: string;
}

interface MediaGalleryModalContext {
  showModal: (content: IMediaGalleryModal) => void;
  hideModal: () => void;
}

export const MediaGalleryModalContext = createContext<MediaGalleryModalContext | undefined>(
  undefined,
);

export const useMediaGalleryModalContext = (): MediaGalleryModalContext => {
  const context = useContext(MediaGalleryModalContext);
  if (context === undefined) {
    throw new Error(
      'MediaGalleryModalContext must be used within a MediaGalleryModalContext.Provider',
    );
  }
  return context;
};

export const MediaGalleryModalContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [modalContent, setModalContent] = useState<IMediaGalleryModal | null>(null);
  const toggleMediaGallery = useFeatureRollout('media_gallery_uplift').isEnabled();

  const modalActions = useMemo(
    () => ({
      showModal: (content: IMediaGalleryModal) => {
        setModalContent(content);
        if (window.location.hash !== mediaGalleryHash) {
          // push media gallery hash to history, allows user to use browser back navigation to close the modal
          // using native history api to avoid re-fetching data, triggering page view among other side effects
          window.history.pushState(null, '', mediaGalleryHash);
        }
      },
      hideModal: () => {
        setModalContent(null);
        if (window.location.hash === mediaGalleryHash) {
          window.history.pushState(null, '', window.location.pathname + window.location.search);
        }
      },
    }),
    [setModalContent],
  );

  // Handle initial load with hash
  useEffect(() => {
    if (window.location.hash === mediaGalleryHash) {
      setModalContent({ openAction: 'DeepLink' });
    }
  }, []);

  // Handle browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      const hasMediaGalleryHash = window.location.hash === mediaGalleryHash;
      const isModalOpen = modalContent !== null;

      if (hasMediaGalleryHash && !isModalOpen) {
        // User navigated forward to media gallery hash - open modal
        setModalContent({ openAction: 'DeepLink' });
      } else if (!hasMediaGalleryHash && isModalOpen) {
        // User navigated back from media gallery hash - close modal
        setModalContent(null);

        // Trigger a full page reload to fire page view tracking
        window.location.reload();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [modalContent]);

  return (
    <MediaGalleryModalContext.Provider value={modalActions}>
      {toggleMediaGallery && modalContent && (
        <MediaGalleryModal
          opened
          onClose={() => modalActions.hideModal()}
          initialImageIndex={modalContent.initialImageIndex}
          galleryItemKey={modalContent.galleryItemKey}
          openAction={modalContent.openAction}
        />
      )}
      {!toggleMediaGallery && modalContent && (
        <MediaGalleryLightBox
          opened
          onClose={() => modalActions.hideModal()}
          openAction={modalContent.openAction}
          initialImageIndex={modalContent.initialImageIndex}
          galleryItemKey={modalContent.galleryItemKey}
        />
      )}
      {children}
    </MediaGalleryModalContext.Provider>
  );
};
